import {
  Component,
  NgZone,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
  Renderer2,
  ChangeDetectionStrategy,
  ElementRef,
  Input,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  ContentChildren,
  QueryList,
} from '@angular/core';
import { PhxFilterService } from '../../services/phx-filter-service';
import { PhxContextService } from '../../services/phx-context-service';
import { ChevronDown } from '../icon/icon-map';
import { PhxDividerComponent } from '../divider/phx-divider.component';
import {
  MOBILE_SCREEN_WIDTH,
  FilterOverflowResult,
  FilterPullBackResult,
  computeOverflowingFilters,
  computePullBack,
  getInitialFiltersWidthSum,
  shouldSkipMoveToDropdown,
} from './phx-filter-row.utils';

@Component({
  selector: 'phx-filter-row',
  templateUrl: './phx-filter-row.component.html',
  styleUrls: ['./phx-filter-row.component.scss'],
  host: {
    class: 'phx-filter-row',
    '[class.phx-filter-row-show-border]': 'phxShowBorder',
    tabIndex: '-1',
  },
  providers: [{ provide: PhxContextService, useClass: PhxFilterService }],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhxFilterRowComponent implements AfterViewInit, OnDestroy {
  @Input() public phxLoading: boolean = false;
  @Input() public phxShowBorder: boolean = false;
  @Output() public phxOnResetClick: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('filterRowNew') filterRowNew: ElementRef<HTMLDivElement>;
  @ViewChild('mainFilters') mainFilters: ElementRef<HTMLDivElement>;
  @ViewChild('rightFilters') rightFilters: ElementRef<HTMLDivElement>;
  @ViewChild('moreFilter') moreFilter: ElementRef<HTMLDivElement>;
  @ViewChild('moreFilterDropdown') moreFilterDropdown: ElementRef<HTMLDivElement>;
  @ContentChildren(PhxDividerComponent) phxDividerComponent!: QueryList<PhxDividerComponent>;

  public isFilterRowExpanded: boolean;
  public filterRowSkeleton: any[] = [];
  public selectedFiltersCountInDropdown: number = 0;
  public isDropdownOpen: boolean = false;
  public readonly ChevronDownIcon: string = ChevronDown;

  private hiddenElementsWidths: number[] = [];
  private previousWindowWidth: number = window.innerWidth;
  private attributeObserver: MutationObserver;
  private resizeObserver: ResizeObserver;

  constructor(private readonly ngZone: NgZone, private renderer: Renderer2, private host: ElementRef<HTMLElement>, private cdr: ChangeDetectorRef) { }

  public ngAfterViewInit(): void {
    if (window.innerWidth >= 768) {
      this.adjustFilterRowContent();
      this.setupAttributeObserver();
      this.setupResizeObserver();
    }
  }

  public ngOnDestroy(): void {
    this.cleanupAttributeObserver();
    this.cleanupResizeObserver();
  }

  public adjustFilterRowContent(shouldPrepend: boolean = false): void {
    if (this.phxLoading || !this.filterRowNew) return;
    
    const filterRow: HTMLElement = this.filterRowNew.nativeElement;
    const filterRowRect: DOMRect = filterRow.getBoundingClientRect();
    const filterRowStyles: CSSStyleDeclaration = getComputedStyle(filterRow);
    
    const filterRowWidth: number = filterRowRect.width - parseFloat(filterRowStyles.paddingLeft) - parseFloat(filterRowStyles.paddingRight);
    const moreFilterElement: HTMLElement = this.moreFilter.nativeElement;
    const moreFilterWidth: number = moreFilterElement.getBoundingClientRect().width || 0;
    const rightFiltersWidth: number = this.rightFilters.nativeElement.getBoundingClientRect().width;
    const mainFilterElement: Element = this.mainFilters?.nativeElement;
    const mainFilterChildren: Element[] = Array.from(mainFilterElement?.children || []);
    const dropdownElement: HTMLElement = this.moreFilterDropdown.nativeElement;
    const dropdownChildren: Element[] = Array.from(dropdownElement.children);

    if (window.innerWidth >= MOBILE_SCREEN_WIDTH) {
      const rawItemWidths: number[] = mainFilterChildren.map((item) => item.getBoundingClientRect().width);

      // First pass: calculate all widths and determine which elements need to be moved
      const overflow: FilterOverflowResult = computeOverflowingFilters(
        rawItemWidths,
        filterRowWidth,
        getInitialFiltersWidthSum(rightFiltersWidth, moreFilterWidth)
      );
      const filtersWidthSum: number = overflow.filtersWidthSum;

      this.hiddenElementsWidths.push(...overflow.hiddenWidths);
      let elementsToMove: Element[] = overflow.overflowIndexes.map((index) => mainFilterChildren[index]);

      // Skip the More button ONLY if:
      // 1. Only 1 element would overflow
      // 2. That element is smaller than More button width
      // 3. Dropdown is currently EMPTY (initial load, not resize with existing items)
      if (shouldSkipMoveToDropdown(overflow.overflowIndexes, rawItemWidths, moreFilterWidth, dropdownChildren.length)) {
        elementsToMove = [];
        this.hiddenElementsWidths.length = 0;
      }

      // Second pass: batch move all elements that need to go to dropdown
      if (elementsToMove.length > 0) {
        const fragment = document.createDocumentFragment();

        if (shouldPrepend) {
          elementsToMove.forEach(item => {
            fragment.appendChild(item);
          });
          const firstChild = dropdownElement.firstChild;
          if (firstChild) {
            this.renderer.insertBefore(dropdownElement, fragment, firstChild);
          } else {
            this.renderer.appendChild(dropdownElement, fragment);
          }
        } else {
          elementsToMove.forEach(item => {
            this.renderer.appendChild(dropdownElement, item);
          });
        }
      }

      if (this.hiddenElementsWidths.length && !this.phxLoading) {
        const pullBack: FilterPullBackResult = computePullBack(
          dropdownChildren.length,
          this.hiddenElementsWidths,
          filtersWidthSum,
          filterRowWidth
        );

        dropdownChildren.slice(0, pullBack.pullBackCount).forEach((item) => {
          this.renderer.appendChild(mainFilterElement, item);
        });
        this.hiddenElementsWidths = pullBack.hiddenWidths;
        this.renderer.setStyle(moreFilterElement, 'display', 'inline-block');
      } else {
        this.renderer.setStyle(moreFilterElement, 'display', 'none');
        this.isDropdownOpen = false;
      }
    } else {
      dropdownChildren.forEach((item) => {
        this.renderer.appendChild(mainFilterElement, item);
      });
      this.hiddenElementsWidths = [];
      this.renderer.setStyle(moreFilterElement, 'display', 'none');
    }
  }

  public updateMoreFilterDropdownCount(): void {
    this.selectedFiltersCountInDropdown = this.moreFilterDropdown.nativeElement.querySelectorAll('[phx-non-default-selected=true]').length;
    this.cdr.detectChanges();
  }

  private setupResizeObserver(): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.ngZone.run(() => {
        const shouldNotPrepend: boolean = this.previousWindowWidth < MOBILE_SCREEN_WIDTH && window.innerWidth >= MOBILE_SCREEN_WIDTH;
        this.adjustFilterRowContent(!shouldNotPrepend);
        this.previousWindowWidth = window.innerWidth;
      });
    });

    this.resizeObserver.observe(this.filterRowNew.nativeElement);
  }

  private cleanupResizeObserver(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  private setupAttributeObserver(): void {
    this.attributeObserver = new MutationObserver((mutations: MutationRecord[]) => {
      const shouldUpdateCount = mutations.some(
        (mutation) =>
          (mutation.type === 'attributes' && mutation.attributeName === 'phx-non-default-selected') || mutation.type === 'childList'
      );

      if (shouldUpdateCount) {
        this.updateMoreFilterDropdownCount();
      }
    });

    const dropdownElement: HTMLElement = this.moreFilterDropdown.nativeElement;

    if (dropdownElement) {
      this.attributeObserver.observe(dropdownElement, {
        attributes: true,
        attributeOldValue: true,
        childList: true,
        subtree: true,
        attributeFilter: ['phx-non-default-selected'],
      });
    }
  }

  private cleanupAttributeObserver(): void {
    if (this.attributeObserver) {
      this.attributeObserver.disconnect();
      this.attributeObserver = null;
    }
  }
}
